/**
 * The field catalogue — slice 67's client surface.
 *
 *   fetchFieldCatalogue   GET    /fields                       every field, grouped, with coverage
 *   updateFieldSettings   PUT    /fields/:key/settings         a person's correction: label, type, date format, hidden
 *   listDerivedFields     GET    /fields/derived               the fields a person defined
 *   createDerivedField    POST   /fields/derived               Age from <any date column>, bands, month, weekday
 *   deleteDerivedField    DELETE /fields/derived/:id           refused (409) while a rule segment or a live flow reads it
 *
 * **The catalogue is never cached here.** It is computed on read by the API —
 * every column of every ready upload, with coverage over the people as they are
 * now — so a module-level copy would go stale the moment an upload finished, and
 * (the house rule) a module-scope cache outlives the tenant that filled it. A
 * screen that needs it asks for it; the builder asks once per mount.
 *
 * **A key travels URL-encoded.** Column names are verbatim (`extras.Joining
 * date`, `extras.Loyalty tier / 2026`), so a key is passed through
 * `encodeURIComponent` before it becomes a path segment — the contract
 * (`fieldKeyParamsSchema`) says the router decodes it. A key with a `/` in it
 * would otherwise route to a different endpoint, and a key with `?` would lose
 * its tail to the query string.
 *
 * Request bodies are re-validated against the same contract the API validates
 * against, so a body it could never accept never leaves the browser.
 */
import {
  createDerivedFieldRequestSchema,
  derivedFieldListResponseSchema,
  derivedFieldResponseSchema,
  fieldCatalogueResponseSchema,
  fieldResponseSchema,
  okResponseSchema,
  updateFieldSettingsRequestSchema,
  type CreateDerivedFieldRequest,
  type DerivedField,
  type Field,
  type FieldCatalogueResponse,
  type OkResponse,
  type UpdateFieldSettingsRequest,
} from '@wend/shared';
import type { z } from 'zod';

import { apiRequest } from './client';

/** `GET /fields` — the whole catalogue, the suggestions, and the people it was measured over. */
export async function fetchFieldCatalogue(signal?: AbortSignal): Promise<FieldCatalogueResponse> {
  return apiRequest({ path: '/fields', schema: fieldCatalogueResponseSchema, signal });
}

/**
 * `PUT /fields/:key/settings` — any subset of label, type, date format and
 * hidden; `null` clears a correction back to what the data says. Answers with
 * the field as it now reads, so the row redraws without a second request.
 */
export async function updateFieldSettings(key: string, request: z.input<typeof updateFieldSettingsRequestSchema>): Promise<Field> {
  const body: UpdateFieldSettingsRequest = updateFieldSettingsRequestSchema.parse(request);
  const response = await apiRequest({
    path: `/fields/${encodeURIComponent(key)}/settings`,
    method: 'PUT',
    body,
    schema: fieldResponseSchema,
  });
  return response.field;
}

/** `GET /fields/derived`. */
export async function listDerivedFields(signal?: AbortSignal): Promise<DerivedField[]> {
  const response = await apiRequest({ path: '/fields/derived', schema: derivedFieldListResponseSchema, signal });
  return response.derivedFields;
}

/** `POST /fields/derived` — the source can be any field of the right type, not a fixed list. */
export async function createDerivedField(request: z.input<typeof createDerivedFieldRequestSchema>): Promise<DerivedField> {
  const body: CreateDerivedFieldRequest = createDerivedFieldRequestSchema.parse(request);
  const response = await apiRequest({
    path: '/fields/derived',
    method: 'POST',
    body,
    schema: derivedFieldResponseSchema,
  });
  return response.derivedField;
}

/**
 * `DELETE /fields/derived/:id`. A 409 means a rule segment or a live flow still
 * reads it; the API's message names them, and the caller shows that message
 * rather than a generic conflict.
 */
export async function deleteDerivedField(derivedFieldId: string): Promise<OkResponse> {
  return apiRequest({
    path: `/fields/derived/${encodeURIComponent(derivedFieldId)}`,
    method: 'DELETE',
    schema: okResponseSchema,
  });
}
